import DSGSearchCategories from "@DSG/Components/DSGSearchCategories";
import CircleClose from "@DSG/Icons/CircleClose";
import colorPallete, {
    bodyFonts,
    elevation,
    headlineFonts,
    labelFonts,
    themeBreakPointSize,
    themeBreakPointValues,
} from "@DSG/MobiusOverrides/styles/variables";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@insite/content-library/Widgets/Header/SearchHistory";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Search from "@insite/mobius/Icons/Search";
import { Button, Clickable, GridContainer, GridItem, LazyImage, Link, Typography } from "@insite/mobius/index";
import Popover, {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React from "react";
import ReactDOM from "react-dom";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    showAutocomplete: boolean;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    searchWrapper?: InjectableCss;
    topResultText?: InjectableCss;
    bottomViewAllLink: InjectableCss;
    categoryNoResultFounds?: InjectableCss;
    noCategoryFoundText?: InjectableCss;
    noResultFullWidth?: InjectableCss;
    noResultFullWidthHeading?: InjectableCss;
    noResultFullWidthSubHeading?: InjectableCss;
    searchContentInner?: InjectableCss;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: { src: Search },
        border: "underline",
        cssOverrides: {
            formField: css`
                width: 250px;
                transition: all .2s ease-in-out;
            `,
        },
    },
    popover: {
        transitionDuration: "regular",
        toggle: false,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
            [class*="PopoverStyle"] {
                &:not(:empty) {
                    max-width: 940px;
                    min-height: 534px;
                    width: 100%;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            [class*="PopoverStyle"] {
                                &:not(:empty) {
                                    margin-right: -20px;
                                    max-width: initial;
                                    min-height: initial;
                                    width: auto;
                                    height: auto;
                                    overflow: initial;
                                    margin-top: 9px;
                                }
                                .searchContent {
                                    &:not(:empty) {
                                        min-height: unset
                                        max-width: 944px;
                                        width: 944px;
                                        background: ${colorPallete.white};
                                        box-shadow: ${elevation.dp08};
                                        border: 1px solid ${colorPallete.grey300};
                                        border-top: none;
                                        border-radius: 0 0 8px 8px;
                                    }
                                }
                            }
                        `,
                    ],
                    "min",
                )}

            @media screen and (min-width: 1024px) and (max-width: 1280px) {
                [class*="PopoverStyle"] {
                    &:not(:empty) {
                        max-width: 850px;
                    }
                    .searchContent {
                        .autocomplete-wrapper {
                            column-gap: 16px;
                        }
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            [class*="PopoverStyle"] {
                                &:not(:empty) {
                                    min-height: initial;
                                    width: 100%;
                                    height: calc(100% - 88px);
                                    max-height: initial;
                                    padding: 0;
                                    margin: 0;
                                }
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    searchWrapper: {
        css: css`
            position: relative;
            .hideBtn {
                display: none;
            }
            .primaryLink {
                &,
                span {
                    color: ${colorPallete.primaryRed500};
                    font: ${labelFonts.label2Regular.fontWeight} ${labelFonts.label2Regular.fontSize} /
                        ${labelFonts.label2Regular.lineHeight} ${labelFonts.label2Regular.fontFamily};
                }
                &.linkMd {
                    &,
                    span {
                        font-size: ${bodyFonts.body2SemiBoldSm.fontSize};
                        line-height: ${bodyFonts.body2SemiBoldSm.lineHeight};
                    }
                }
                &.bold {
                    &,
                    span {
                        font-weight: ${labelFonts.label1SemiBold.fontWeight};
                    }
                }
                &:hover {
                    &,
                    span {
                        color: ${colorPallete.primaryMaroon500};
                    }
                }
                &:active {
                    &,
                    span {
                        color: ${colorPallete.grey800};
                    }
                }
            }
            .textUnderline {
                span {
                    text-decoration: underline;
                }
            }
            .d-block {
                display: block;
            }
            .secondaryLink {
                &,
                span {
                    color: ${colorPallete.grey800};
                    font: ${labelFonts.label1Regular.fontWeight} ${labelFonts.label1Regular.fontSize} /
                        ${labelFonts.label1Regular.lineHeight} ${labelFonts.label1Regular.fontFamily};
                }
                &.linkMd {
                    &,
                    span {
                        font-size: ${labelFonts.label1Regular.fontSize};
                        line-height: ${labelFonts.label1Regular.lineHeight};
                    }
                }
                &.bold {
                    &,
                    span {
                        font-weight: ${labelFonts.label1SemiBold.fontWeight};
                    }
                }
                &:hover {
                    &,
                    span {
                        color: ${colorPallete.primaryMaroon500};
                    }
                }
                &:active {
                    &,
                    span {
                        color: ${colorPallete.grey800};
                    }
                }
            }
            .searchBlockHeading {
                font: ${headlineFonts.headline6SemiBoldLg.fontWeight} ${headlineFonts.headline6SemiBoldLg.fontSize} /
                    ${headlineFonts.headline6SemiBoldLg.lineHeight} ${headlineFonts.headline6SemiBoldLg.fontFamily};
                margin: 0 0 16px;
            }
            .activePopover {
                color: ${colorPallete.grey800};
                [class*="FormFieldStyle--"] {
                    input:not(.react-datetime-picker__inputGroup__input) {
                        border-color: ${colorPallete.grey800};
                    }
                }
            }
            [class*="OverflowWrapper"] {
                [class*="FormFieldStyle--"] {
                    input:not(.react-datetime-picker__inputGroup__input) {
                        &:focus,
                        &:active {
                            border: 2px solid ${colorPallete.grey800};
                        }
                    }
                }
            }
            .closeButton {
                &,
                &:not(:disabled),
                &:hover,
                &:focus,
                &:active {
                    padding: 0;
                    min-height: initial;
                    border: none;
                    background: transparent;
                    position: absolute;
                    right: 8px;
                    top: 8px;
                }
            }
            .cancelButtonPopover {
                &,
                &:not(:disabled),
                &:hover,
                &:focus,
                &:active {
                    padding: 0;
                    min-height: initial;
                    border: none;
                    background: transparent;
                    position: absolute;
                    right: 24px;
                    top: 34px;
                }
                & {
                    span {
                        color: ${colorPallete.primaryRed500};
                        text-decoration: underline;
                        font: ${labelFonts.label1SemiBold.fontWeight} ${labelFonts.label1SemiBold.fontSize}/${labelFonts.label1SemiBold.lineHeight} ${labelFonts.label1SemiBold.fontFamily};
                    }
                    &:hover {
                        span {
                            color: ${colorPallete.primaryMaroon500};
                        }
                    }
                    &:active {
                        span {
                            color: ${colorPallete.grey800};
                        }
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            .activePopover {
                                [class*="PopoverStyle"] {
                                    background: transparent;
                                    box-shadow: none;
                                }
                            }
                            .desktopHidden {
                                display: none;
                            }
                            .searchBlockHeading {
                                margin-bottom: 16px;
                            }
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            [class*="FormFieldStyle--"] {
                                input:not(.react-datetime-picker__inputGroup__input) {
                                    &:focus {
                                        font-size: ${bodyFonts.body1RegularSm.fontSize} !important;
                                    }
                                }
                            }
                            .activePopover {
                                [class*="FormFieldStyle--"]  {
                                    input:not(.react-datetime-picker__inputGroup__input) {
                                        &,
                                        &:focus {
                                            font-size: ${bodyFonts.body1RegularSm.fontSize} !important;
                                        }
                                    }
                                }
                            }
                        `,
                        css`
                            .mobileHidden {
                                display: none;
                            }
                            [class*="OverflowWrapper"] {
                                [class*="FormFieldStyle--"] {
                                    input:not(.react-datetime-picker__inputGroup__input) {
                                        &,
                                        &:focus {
                                            min-height: 40px;
                                        }
                                    }
                                }
                            }
                            .closeButton {
                                &,
                                &:not(:disabled),
                                &:hover,
                                &:focus,
                                &:active {
                                    right: 89px;
                                    top: 28px;
                                }
                            }
                            .activePopover {
                                position: fixed;
                                width: 100%;
                                background: ${colorPallete.white};
                                height: 100%;
                                overflow-y: auto;
                                top: 0;
                                left: 0;
                                z-index: 999;
                                display: block;
                                min-height: 100%;
                                overflow: hidden;
                                overflow-y: auto;
                                [class*="PopoverStyle"] {
                                    position: initial !important;
                                    top: auto !important;
                                    width: 100%;
                                    box-shadow: none;
                                    overflow: initial;
                                    .searchContent {
                                        padding: 16px;
                                        height: 100%;
                                    }
                                }
                                [class*="FormFieldStyle--"] {
                                    padding: 24px 84px 24px 24px;
                                    background: ${colorPallete.grey100};
                                }
                            }

                            .activePopover .cancelButtonPopover {
                                display: block;
                                z-index: 2;
                            }
                            .cancelButtonPopover,
                            .closeButton {
                                position: absolute;
                                top: 0;
                                z-index: 1;
                                right: 0;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-direction: column-reverse;
                            padding: 0 0 16px;
                        `,
                    ],
                    "max",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            column-gap: 40px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            width: 100%;
                            &.autocomplete-column-left {
                                max-width: 216px;
                                .categoryWrapper {
                                    min-height: 408px;
                                }
                                > div {
                                    display: flex;
                                    flex-direction: column;
                                    height: 100%;
                                    .desktopOnlyBorder {
                                        height: 100%;
                                    }
                                }
                            }
                            &.autocomplete-column-right {
                                max-width: 604px;
                            }
                            .desktopOnlyBorder {
                                border: 1px solid ${colorPallete.grey300};
                                padding: 16px;
                                border-radius: 8px;
                            }
                            &.autocomplete-column-left {
                                a {
                                    color: ${colorPallete.grey100};
                                }
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    topResultText: {
        css: css`
            display: flex;
            flex-direction: column;
            a {
                justify-content: flex-end;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            flex-direction: row;
                            justify-content: space-between;
                            align-items: flex-start;
                            a {
                                min-width: 125px;
                                max-width: 125px;
                                maring-left: 20px;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    bottomViewAllLink: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            margin-bottom: 40px;
                        `,
                    ],
                    "max",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            display: flex;
                            justify-content: center;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    categoryNoResultFounds: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            border: 1px solid ${colorPallete.grey300};
                            border-radius: 8px;
                            padding: 16px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    noCategoryFoundText: {
        css: css`
            color: ${colorPallete.primaryRed500};
            display: block;
            font: ${labelFonts.label1SemiBold.fontWeight} ${labelFonts.label1SemiBold.fontSize} /
                ${labelFonts.label1SemiBold.lineHeight} ${labelFonts.label1SemiBold.fontFamily};
        `,
    },
    noResultFullWidthHeading: {
        css: css`
            font: ${headlineFonts.headline4SemiBoldSm.fontWeight} ${headlineFonts.headline4SemiBoldSm.fontSize} /
                ${labelFonts.label2SemiBold.lineHeight} ${headlineFonts.headline4SemiBoldSm.fontFamily};
            margin-bottom: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            font-size: ${headlineFonts.headline4SemiBoldLg.fontSize};
                            line-height: ${headlineFonts.headline4SemiBoldLg.lineHeight};
                        `,
                    ],
                    "min",
                )}
        `,
    },
    noResultFullWidth: {
        css: css`
            color: ${colorPallete.grey800};
            padding: 40px 0 24px;
            margin-bottom: 40px;
            display: flex;
            flex-direction: column;
            span {
                text-align: center;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            min-height: 437px;
                            width: 100%;
                            justify-content: center;
                            margin: 0;
                            padding: 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    noResultFullWidthSubHeading: {
        css: css`
            font: ${bodyFonts.body1SemiBoldSm.fontWeight} ${bodyFonts.body1SemiBoldSm.fontSize} /
                ${bodyFonts.body1SemiBoldSm.lineHeight} ${bodyFonts.body1SemiBoldSm.fontFamily};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            font-size: ${bodyFonts.body1RegularLg.fontSize};
                            line-height: ${bodyFonts.body1RegularLg.lineHeight};
                        `,
                    ],
                    "min",
                )}
        `,
    },
    searchContentInner: {
        css: css`
            padding: 24px;
            display: flex;
            column-gap: 40px;

            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                css`
                    padding: 0;
                    column-gap: 0;
                    row-gap: 40px;
                    flex-direction: column;
                `,
                null,
                null,
            ], "max")}
        `
    }
};

export interface DSGTopCategoriesStyles {
    wrapperContainer: GridContainerProps;
    categoryItem: GridItemProps;
    categoryTitle: TypographyPresentationProps;
    clickable: ClickablePresentationProps;
    searchHelpWrapper: InjectableCss;
}

export const topCategoriesStyles: DSGTopCategoriesStyles = {
    wrapperContainer: {
        offsetCss: css`
            gap: 16px;
            width: 100%;
            margin: 0;
        `,
        css: css`
            > ${GridOffset} {
                width: 100%;
                justify-content: center;
                align-items: stretch;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                gap: 24px;
                            `,
                            null,
                        ],
                        "min",
                    )}
            }
            ${GridItemStyle} {
                justify-content: center;
                text-align: center;
                border: 1px solid ${colorPallete.grey500};
                border-radius: 8px;
                padding: 16px;
                max-width: 163px !important;
                flex-basis: 163px !important;
                @media only screen and (min-width: 540px) {
                    max-width: 185px !important;
                    flex-basis: 185px !important;
                }
                &:hover {
                    border-color: ${colorPallete.grey800};
                    box-shadow: ${elevation.dp04};
                }
            }
        `,
    },
    categoryItem: {
        width: [6, 6, 4, 4, 4],
        css: css`
            [class*="LazyImageStyle"] {
                padding: 0 !important;
            }
        `,
    },
    categoryTitle: {
        size: labelFonts.label1SemiBold.fontSize,
        lineHeight: labelFonts.label1SemiBold.lineHeight,
        weight: labelFonts.label1SemiBold.fontWeight,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${bodyFonts.body1SemiBoldSm.fontSize};
                            line-height: ${bodyFonts.body1SemiBoldSm.lineHeight};
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    clickable: {
        css: css`
            flex-direction: column;
            img {
                margin-bottom: 8px;
                max-width: 153px;
                min-height: 97px;
                max-height: 97px;
                object-fit: cover;
            }
        `,
    },
    searchHelpWrapper: {
        css: css`
            padding: 0 40px 40px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 0 0 40px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}

            .search-help-container {
                display: flex;
                align-items: center;
                padding: 16px 24px;
                background: ${colorPallete.grey100};
                border-radius: 8px;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding: 16px;
                                flex-direction: column;
                                align-items: flex-start;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}

                p {
                    font-size: ${headlineFonts.headline6SemiBoldLg.fontSize};
                    line-height: ${headlineFonts.headline6SemiBoldLg.lineHeight};
                    font-weight: ${headlineFonts.headline6SemiBoldLg.fontWeight};
                    padding-right: 8px;
                    flex-shrink: 0;
                    margin-bottom: 0;
                }

                .search-help-links {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    list-style: none;
                    margin: 0;
                    padding-left: 0;

                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    flex-direction: column;
                                    align-items: flex-start;
                                    margin-top: 16px;
                                `,
                                null,
                                null,
                            ],
                            "max",
                        )}

                    li {
                        a {
                            display: inline-block;
                            padding: 0 8px;
                            border-left: 1px solid ${colorPallete.grey400};
                            &,
                            & span {
                                font-size: ${labelFonts.label1SemiBold.fontSize};
                                line-height: ${labelFonts.label1SemiBold.lineHeight};
                                font-weight: ${labelFonts.label1SemiBold.fontWeight};
                                color: ${colorPallete.primaryRed500};
                            }

                            &:hover {
                                &, & span {
                                    color: ${colorPallete.primaryMaroon500};
                                }
                            }

                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(
                                    theme,
                                    [
                                        null,
                                        css`
                                            padding: 0;
                                            border: none;
                                        `,
                                        null,
                                        null,
                                    ],
                                    "max",
                                )}
                        }

                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        line-height: ${labelFonts.label2SemiBold.lineHeight};
                                        &:not(:last-child) {
                                            padding: 0 0 8px;
                                        }
                                    `,
                                    null,
                                    null,
                                ],
                                "max",
                            )}
                    }
                }
            }
        `,
    },
};

const categoryStyles = topCategoriesStyles;

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);

        this.state = {
            query: "",
            focusableAutocompleteItems: [],
            isLoading: false,
            showAutocomplete: false,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            this.popover.current && (this.popover.current! as any).closePopover();
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        // this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            this.goToUrl(this.state.autoCompleteModel?.products[0].url);
        } else {
            const encodedQuery = encodeURIComponent(this.state.query);
            this.goToUrl(`/${this.props.searchPath}?query=${encodedQuery}`, false);
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }
    };

    loadAutocomplete = debounce((query: string) => {
        this.props.getAutocompleteModel({
            query,
            onSuccess: result => {
                if (this.showAutocomplete) {
                    this.setState({
                        autoCompleteModel: result,
                    });
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        this.loadAutocomplete(newQuery);
    };

    mobileOnlyPopover = () => {};
    
    clearSearchInput = () => {
        this.setState({ query: "" });
        this.setState({ showAutocomplete: true });
    };

    removeFocus = (event: React.KeyboardEvent) => {
        if (event && event.keyCode === 13 && event.currentTarget instanceof HTMLElement) {
            event.currentTarget.blur();
        }
    }

    cancelSearchPopover = () => {
        this.setState({ query: "" });
        if (this.popover.current) {
            this.popover.current.closePopover();
        }
    };

    openAutocomplete = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    closeAutocomplete = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
        this.clearSearchInput();
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: KeyboardEvent) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        this.props.history.push(url);
    };

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const encodedQuery = encodeURIComponent(this.state.query);
        return (
            <>
                <StyledWrapper {...this.styles.autocompleteWrapper} className="autocomplete-wrapper">
                    {((categories && categories.length === 0) ||
                        (brands && brands.length === 0) ||
                        (content && content.length === 0)) &&
                    products &&
                    products.length === 0 ? (
                        <StyledWrapper {...this.styles.noResultFullWidth}>
                            <Typography {...this.styles.noResultFullWidthHeading}>
                                {translate("No results found for:")}
                            </Typography>
                            <Typography {...this.styles.noResultFullWidthSubHeading}>“{this.state.query}”</Typography>
                        </StyledWrapper>
                    ) : (
                        <>
                            <StyledWrapper
                                {...this.styles.autocompleteColumnWrapper}
                                className="autocomplete-column-wrapper autocomplete-column-left"
                            >
                                {categories && categories.length > 0 ? (
                                    <AutocompleteCategories
                                        categories={categories}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    />
                                ) : (
                                    <StyledWrapper {...this.styles.categoryNoResultFounds}>
                                        <Typography as="h2" className="searchBlockHeading">
                                            {translate("Related Categories")}
                                        </Typography>
                                        <StyledWrapper className="desktopOnlyBorder categoryWrapper">
                                            <Typography {...styles.noCategoryFoundText}>
                                                {translate("No category results found")}
                                            </Typography>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                                {/* {((content && content.length > 0)) && (
                                <AutocompleteContent
                                    content={content}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteContentStyles}
                                />
                            )}      */}
                            </StyledWrapper>

                            {products && products.length > 0 ? (
                                <StyledWrapper
                                    {...this.styles.autocompleteColumnWrapper}
                                    className="autocomplete-column-wrapper autocomplete-column-right"
                                >
                                    <Typography as="h2" className="searchBlockHeading" {...this.styles.topResultText}>
                                        <Typography>
                                            {translate("Top results for")} “{this.state.query}”
                                        </Typography>
                                        <Link
                                            href={`${this.props.searchPath}?query=${encodedQuery}`}
                                            className="primaryLink linkMd bold textUnderline"
                                            onClick={this.cancelSearchPopover}
                                        >
                                            {translate("View all results")}
                                        </Link>
                                    </Typography>
                                    {/* Disabled below code temperorily for static categories styles. */}

                                    <AutocompleteProducts
                                        products={products}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteProductsStyles}
                                    />
                                    <StyledWrapper {...this.styles.bottomViewAllLink}>
                                        <Link
                                            href={`${this.props.searchPath}?query=${encodedQuery}`}
                                            className="primaryLink linkMd bold textUnderline"
                                            onClick={this.cancelSearchPopover}
                                        >
                                            {translate("View all results")}
                                        </Link>
                                    </StyledWrapper>
                                </StyledWrapper>
                            ) : (
                                <StyledWrapper {...this.styles.noResultFullWidth}>
                                    <Typography {...this.styles.noResultFullWidthHeading}>
                                        {translate("No results found for:")}
                                    </Typography>
                                    <Typography {...this.styles.noResultFullWidthSubHeading}>
                                        “{this.state.query}”
                                    </Typography>
                                </StyledWrapper>
                            )}
                        </>
                    )}
                </StyledWrapper>
            </>
        );
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}`;

        const searchInput = (
            <TextField
                {...styles.input}
                ref={this.props.inputRef}
                isAnimated={this.props.isAnimated}
                iconClickableProps={{ onClick: this.doSearch }}
                value={this.state.query}
                onChange={this.handleInputChange}
                onFocus={this.clearSearchInput}
                placeholder={translate("Search by product name or part number")}
                autoComplete="off"
                clickableText={translate("submit search")}
                id={searchId}
                data-test-selector="headerSearchInputTextField"
                className="search-input"
                onKeyDown={this.removeFocus}
                role="combobox"
            />
        );

        return (
            <>
                <StyledWrapper className="searchWrapper" {...styles.searchWrapper}>
                    <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                        {translate("Site Search")}
                    </VisuallyHidden>

                    <Popover
                        {...styles.popover}
                        controlsId={`${searchId}-popover`}
                        ref={this.popover}
                        insideRefs={[this.list]}
                        popoverTrigger={searchInput}
                        positionFunction={this.getPopoverPosition}
                        wrapperProps={styles.popoverWrapper}
                        contentBodyProps={
                            {
                                ref: this.list,
                                ...styles.popoverContentBody,
                            } as any
                        }
                        handleKeyDown={this.handlePopoverKeyDown}
                        onClose={this.handlePopoverClose}
                        className="popover"
                    >
                        <StyledWrapper className="searchContent">
                            <StyledWrapper {...styles.searchContentInner}>
                                {(!this.state.query || this.state.query.length < 3) && this.showAutocomplete && (
                                    <SearchHistory
                                        focusedQuery={this.state.focusedSearchHistoryQuery}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={styles.searchHistoryStyles}
                                    />
                                )}
                                {this.state.query && this.state.query.length >= 3 && this.renderAutocomplete()}
                                {(!this.state.query || this.state.query.length < 3) && this.showAutocomplete &&(
                                    <DSGSearchCategories />
                                )}
                            </StyledWrapper>
                            <StyledWrapper className="search-help-wrapper" {...categoryStyles.searchHelpWrapper}>
                                {siteMessage("Dsg_SearchHelpWidgetMessage")}
                            </StyledWrapper>
                        </StyledWrapper>

                        {this.state.query.length > 0 && (
                            <Button className="closeButton mobileOnly desktopHidden" onClick={this.clearSearchInput}>
                                <VisuallyHidden>{translate("Close")}</VisuallyHidden>
                                <CircleClose />
                            </Button>
                        )}

                        <Button className="cancelButtonPopover" onClick={this.cancelSearchPopover}>
                            {translate("Cancel")}
                        </Button>
                    </Popover>
                    {this.state.query.length > 0 && (
                        <Button className="closeButton desktopOnly mobileHidden" onClick={this.clearSearchInput}>
                            <VisuallyHidden>{translate("Close")}</VisuallyHidden>
                            <CircleClose />
                        </Button>
                    )}
                </StyledWrapper>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
